import { InvalidContentError, isContentFragment } from '@oneaudi/falcon-tools';
import React from 'react';
import type { FalconContent, StageProps, StagePropsFACE } from '../../types';
import { CTAsTheme, Theme } from '../../types';

import { Context } from '../Context';

export const transformCmsDataToProps = (content: StagePropsFACE | FalconContent): StageProps => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loggerService } = React.useContext(Context);

  if (!content || Object.keys(content).length === 0) {
    loggerService?.warn('No Editor content found for Stage');

    return {} as StageProps;
  }

  if (content.__type !== 'aem-headless') {
    const {
      asset,
      appearance,
      text,
      buttons,
      consumptionAndEmissions,
      priceFootnotes,
      prices,
      disclaimers,
      appearancemodelPage,
    } = content;
    return {
      asset,
      appearance: {
        ...appearance,
        theme: appearance.theme || Theme.Dark,
        backgroundDisclaimersCTAs: appearance.backgroundDisclaimersCTAs || CTAsTheme.Dark,
      },
      text,
      buttons,
      consumptionAndEmissions,
      priceFootnotes,
      prices,
      disclaimers,
      appearancemodelPage,
      fascinationMode: false,
    };
  }

  return convertFalconContentToStageProps(content);
};

function isFalconContent(content: unknown): content is FalconContent {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
  return Boolean(isContentFragment(content));
}

const convertFalconContentToStageProps = (content: FalconContent): StageProps => {
  if (!isFalconContent(content)) {
    throw new InvalidContentError('Content is invalid.');
  }

  const {
    appearanceTheme,
    appearanceBackgroundDisclaimersCTAs,
    appearanceTextContrast,
    appearanceEnableShader,
    appearanceContentPosition,
    appearanceMode,
    asset,
    firstButtonLabel,
    firstButtonUrl,
    firstButtonAriaLabel,
    firstButtonOpenInTabOrLayer,
    firstChatBotId,
    firstButtonVariant,
    secondButtonLabel,
    secondButtonUrl,
    secondButtonAriaLabel,
    secondButtonOpenInTabOrLayer,
    secondChatBotId,
    consumptionAndEmissions,
    priceFootnotes,
    disclaimers,
    totalPrice,
    textHeadline,
    textHeadingTag,
    textSubline,
    appearancemodelPage,
    fascinationMode,
  } = content.fields;

  return {
    appearance: {
      theme: appearanceTheme || Theme.Dark,
      backgroundDisclaimersCTAs: appearanceBackgroundDisclaimersCTAs || CTAsTheme.Dark,
      enableShader: appearanceEnableShader,
      textContrast: appearanceTextContrast,
      contentPosition: appearanceContentPosition,
      mode: appearanceMode,
    },
    asset: {
      assetType: asset?.fields?.assetType,
      landscape: {
        assetValue: asset?.fields?.assetLandscape?.path ?? '',
      },

      landscapeCaptions: {
        assetValue: asset?.fields?.assetLandscapeCaptions?.path ?? '',
      },
      landscapeAudioDescription: {
        assetValue: asset?.fields?.assetLandscapeAudioDescription?.path ?? '',
      },
      landscapeAltText: asset?.fields?.assetAlt,
      landscapePoster: {
        assetValue: asset?.fields?.assetPoster?.path ?? '',
      },
      portrait: {
        assetValue: asset?.fields?.assetPortrait?.path ?? '',
      },
      portraitCaptions: {
        assetValue: asset?.fields?.assetPortraitCaptions?.path ?? '',
      },
      portraitAudioDescription: {
        assetValue: asset?.fields?.assetPortraitAudioDescription?.path ?? '',
      },
      portraitAltText: asset?.fields?.assetPortraitAltText,
      portraitPoster: {
        assetValue: asset?.fields?.assetPortraitPoster?.path ?? '',
      },
      autoPlay: asset?.fields?.assetAutoPlay,
      loop: asset?.fields?.assetLoop,
      preserveVideoWidth: asset?.fields?.assetPreserveVideoWidth,
      hideNativeControlsLandscape: asset?.fields?.assetHideNativeControlsLandscape,
      hideNativeControlsPortrait: asset?.fields?.assetHideNativeControlsPortrait,
    },
    buttons: [
      {
        label: firstButtonLabel,
        url: firstButtonUrl,
        ariaLabel: firstButtonAriaLabel,
        openInTabOrLayer: firstButtonOpenInTabOrLayer,
        chatBotId: firstChatBotId,
        variant: firstButtonVariant,
      },
      {
        label: secondButtonLabel,
        url: secondButtonUrl,
        ariaLabel: secondButtonAriaLabel,
        openInTabOrLayer: secondButtonOpenInTabOrLayer,
        chatBotId: secondChatBotId,
      },
    ],
    consumptionAndEmissions,
    priceFootnotes,
    prices: {
      totalPrice,
    },
    disclaimers,
    text: {
      headline: textHeadline,
      headingTag: textHeadingTag,
      subline: textSubline,
    },
    appearancemodelPage,
    fascinationMode: (!process.env.IS_LEGACY && fascinationMode) || false,
  };
};
